import React, { createContext, useContext, useState, useEffect } from 'react';

interface Company {
  uuid: string;
  name: string;
  subdomain: string;
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  description?: string;
  website?: string;
}

interface CompanyContextType {
  company: Company | null;
  isLoading: boolean;
  error: string | null;
  fetchCompanyBySubdomain: (subdomain: string) => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

interface CompanyProviderProps {
  children: React.ReactNode;
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({ children }) => {
  const [company, setCompany] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCompanyBySubdomain = async (subdomain: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://i9k7icnr44.execute-api.ap-south-1.amazonaws.com/dev';
      const response = await fetch(`${apiUrl}/api/v1/companies/subdomain/${subdomain}`, {
        headers: {
          'X-Api-Key': 'dev-api-key-12345'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          // No company found for subdomain, use default
          setCompany(null);
        } else {
          throw new Error('Failed to fetch company');
        }
      } else {
        const data = await response.json();
        setCompany(data);
        
        // Apply company branding to document
        if (data.primary_color) {
          document.documentElement.style.setProperty('--primary-color', data.primary_color);
        }
        if (data.secondary_color) {
          document.documentElement.style.setProperty('--secondary-color', data.secondary_color);
        }
      }
    } catch (err) {
      console.error('Error fetching company:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setCompany(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Detect subdomain from current URL
    const hostname = window.location.hostname;
    
    // Extract subdomain
    let subdomain = null;
    if (hostname.includes('hirehubble.com')) {
      // Production: subdomain.hirehubble.com
      const parts = hostname.split('.');
      if (parts.length > 2 && parts[0] !== 'www') {
        subdomain = parts[0];
      }
    } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
      // Development: check for subdomain query parameter
      const params = new URLSearchParams(window.location.search);
      subdomain = params.get('subdomain');
    }
    
    // Fetch company if subdomain exists
    if (subdomain) {
      fetchCompanyBySubdomain(subdomain);
    }
  }, []);

  return (
    <CompanyContext.Provider value={{ company, isLoading, error, fetchCompanyBySubdomain }}>
      {children}
    </CompanyContext.Provider>
  );
};